import { createInertiaApp } from '@inertiajs/react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { resolvePageComponent } from './helpers';
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import React from 'react';
import { ReactComponent } from 'node_modules/@inertiajs/react/types/types';
import { preloadElementalBlocks } from './components/global/ElementalArea';

const bugsnagAPIKey = import.meta.env.VITE_FRONTEND_BUGSNAG_API_KEY as string | null || '';

if (bugsnagAPIKey) {
    Bugsnag.start({
        apiKey: bugsnagAPIKey,
        plugins: [new BugsnagPluginReact()],
    });
    BugsnagPerformance.start({ apiKey: bugsnagAPIKey });
}

const FallbackErrorBoundary: BugsnagErrorBoundary = ({ children }) => {
    return <>{children}</>;
};
const ErrorBoundary = Bugsnag.isStarted() ? Bugsnag.getPlugin('react')?.createErrorBoundary(React) ?? FallbackErrorBoundary : FallbackErrorBoundary;


void createInertiaApp({
    title: (title) => `${title}`,
    resolve: (name) => resolvePageComponent<ReactComponent>(`./pages/${name}.tsx`, import.meta.glob('./pages/**/*.tsx') as Record<string, () => Promise<ReactComponent>>),
    setup({ el, App, props }) {
        // SSR renders every block eagerly, but the client resolves each as its own lazy chunk;
        // mounting before those chunks resolve briefly collapses whatever hasn't loaded yet (see
        // preloadElementalBlocks, which is also what makes it safe for ElementalArea to skip
        // React.lazy()/Suspense for anything preloaded here). Kick off the fetches first so
        // they're resolved by the time the tree below reaches them, without delaying the mount
        // on a rejection.
        const mount = process.env.NODE_ENV === 'development'
            ? () => createRoot(el).render(<ErrorBoundary><App {...props} /></ErrorBoundary>)
            : () => hydrateRoot(el, <ErrorBoundary><App {...props} /></ErrorBoundary>);
        void preloadElementalBlocks(props.initialPage.props).finally(mount);
    },
});
